import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Note, NoteTag, NoteWithTodos } from 'interfaces/note.interface';
import { PAGE } from 'constants/general';
import {
  getNotes,
  getUserNoteTags,
  searchNotes,
  getNoteById,
  createOrUpdateNote,
  deleteNote,
  getNoteFolders,
  moveNoteOutOfFolder
} from './extra';
import { initialNotesState } from 'store/initial-states';
import { mergeNotesApiWins, removeNoteById } from 'utils/note.util';

const noteSlice = createSlice({
  name: 'note',
  initialState: initialNotesState,
  reducers: {
    updateNotes: (state, { payload }: PayloadAction<Note[]>) => {
      state.notes = payload;
    },
    updateSelectedNote: (
      state,
      { payload }: PayloadAction<NoteWithTodos | null>
    ) => {
      state.selectedNote = payload;
    },
    updateUserTags: (state, { payload }: PayloadAction<NoteTag[]>) => {
      state.userTags = payload;
    },
    updateIsFetchingNotes: (state, { payload }: PayloadAction<boolean>) => {
      state.isFetchingNotes = payload;
    },
    updateIsCreatingNote: (state, { payload }: PayloadAction<boolean>) => {
      state.isCreatingNote = payload;
    },
    updateIsUpdatingNote: (state, { payload }: PayloadAction<boolean>) => {
      state.isUpdatingNote = payload;
    },
    updateIsDeletingNote: (state, { payload }: PayloadAction<boolean>) => {
      state.isDeletingNote = payload;
    },
    updateShowNoteModal: (state, { payload }: PayloadAction<boolean>) => {
      state.showNoteModal = payload;
    },
    updateShowDeleteConfirmModal: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.showDeleteConfirmModal = payload;
    },
    updateNoteToDelete: (state, { payload }: PayloadAction<string | null>) => {
      state.noteToDelete = payload;
    },
    updateCurrentPage: (state, { payload }: PayloadAction<number>) => {
      state.currentPage = payload;
    },
    updateSearchQuery: (state, { payload }: PayloadAction<string>) => {
      state.searchQuery = payload;
    },
    updateSearchResults: (state, { payload }: PayloadAction<Note[] | null>) => {
      state.searchResults = payload;
    },
    updateIsSearching: (state, { payload }: PayloadAction<boolean>) => {
      state.isSearching = payload;
    },
    /** Null opens Personal notes. The list is cleared so `getNotes` doesn't merge in the previous folder's notes. */
    openNoteFolder: (state, { payload }: PayloadAction<string | null>) => {
      state.activeNoteFolderId = payload;
      state.notes = [];
      state.currentPage = PAGE.FIRST;
      state.hasNextPage = false;
      state.hasLoadedNotes = false;
    },
    resetNoteFolders: (state) => {
      state.noteFolders = null;
      state.hasLoadedNoteFolders = false;
      state.activeNoteFolderId = null;
    },
    resetNoteState: () => initialNotesState
  },
  extraReducers(builder) {
    builder
      .addCase(getNotes.pending, (state) => {
        state.isFetchingNotes = true;
      })
      .addCase(getNotes.fulfilled, (state, action) => {
        const { payload, meta } = action;
        state.isFetchingNotes = false;
        state.hasLoadedNotes = true;
        if (payload) {
          const incomingNotes: Note[] = payload.data ?? [];
          const shouldAppend =
            meta.arg?.append === true &&
            (meta.arg?.page ?? PAGE.FIRST) > PAGE.FIRST;

          state.notes = shouldAppend
            ? [
                ...state.notes,
                ...incomingNotes.filter(
                  (incomingNote) =>
                    !state.notes.some(
                      (existingNote) => existingNote.id === incomingNote.id
                    )
                )
              ]
            : mergeNotesApiWins(incomingNotes, state.notes);
          state.hasNextPage = payload.meta?.hasNextPage ?? false;
          state.hasPreviousPage = payload.meta?.hasPreviousPage ?? false;
          state.currentPage = meta.arg?.page ?? PAGE.FIRST;
        }
      })
      .addCase(getNotes.rejected, (state) => {
        state.isFetchingNotes = false;
        state.hasLoadedNotes = true;
      })
      .addCase(getUserNoteTags.pending, (state) => {
        state.isFetchingTags = true;
      })
      .addCase(getUserNoteTags.fulfilled, (state, { payload }) => {
        state.isFetchingTags = false;
        state.userTags = payload ?? [];
      })
      .addCase(getUserNoteTags.rejected, (state) => {
        state.isFetchingTags = false;
      })
      .addCase(searchNotes.pending, (state) => {
        state.isSearching = true;
      })
      .addCase(searchNotes.fulfilled, (state, { payload }) => {
        state.isSearching = false;
        state.searchResults = payload ?? [];
      })
      .addCase(searchNotes.rejected, (state) => {
        state.isSearching = false;
      })
      .addCase(getNoteById.fulfilled, (state, { payload }) => {
        if (payload) {
          state.selectedNote = payload;
        }
      })
      .addCase(createOrUpdateNote.pending, (state, { meta }) => {
        if (meta.arg.isUpdate) {
          state.isUpdatingNote = true;
        } else {
          state.isCreatingNote = true;
        }
      })
      .addCase(createOrUpdateNote.fulfilled, (state, { meta }) => {
        state.isCreatingNote = false;
        state.isUpdatingNote = false;
        if (meta.arg.payload.is_brain_dump !== true) {
          state.showNoteModal = false;
          state.selectedNote = null;
        }
      })
      .addCase(createOrUpdateNote.rejected, (state) => {
        state.isCreatingNote = false;
        state.isUpdatingNote = false;
      })
      .addCase(deleteNote.pending, (state) => {
        state.isDeletingNote = true;
      })
      .addCase(deleteNote.fulfilled, (state, { payload, meta }) => {
        state.isDeletingNote = false;
        if (payload) {
          state.showDeleteConfirmModal = false;
          state.noteToDelete = null;
          state.notes = removeNoteById(state.notes, meta.arg);
        }
      })
      .addCase(deleteNote.rejected, (state) => {
        state.isDeletingNote = false;
      })
      .addCase(getNoteFolders.fulfilled, (state, { payload }) => {
        state.noteFolders = payload;
        state.hasLoadedNoteFolders = true;
      })
      .addCase(getNoteFolders.rejected, (state) => {
        state.noteFolders = null;
        state.hasLoadedNoteFolders = true;
      })
      .addCase(moveNoteOutOfFolder.fulfilled, (state, { payload, meta }) => {
        if (payload) {
          state.notes = removeNoteById(state.notes, meta.arg);
        }
      });
  }
});

export const {
  updateNotes,
  updateSelectedNote,
  updateUserTags,
  updateIsFetchingNotes,
  updateIsCreatingNote,
  updateIsUpdatingNote,
  updateIsDeletingNote,
  updateShowNoteModal,
  updateShowDeleteConfirmModal,
  updateNoteToDelete,
  updateCurrentPage,
  updateSearchQuery,
  updateSearchResults,
  updateIsSearching,
  openNoteFolder,
  resetNoteFolders,
  resetNoteState
} = noteSlice.actions;

export default noteSlice.reducer;
